/**
 * Modal "Testar no chat" — admin faz pergunta natural sobre catálogo.
 *
 * OpenSpec add-categorized-catalog-guideline (PR 2 Fase 3).
 *
 * Mostra answer + sources citadas (com score). Permite refinar query
 * sem fechar o modal.
 */
import React, { useState } from 'react';
import { askCatalog } from '../../services/catalog';
import type { CatalogAskResponse } from '../../types';

interface Props {
  defaultCategoryId?: string;
  onClose: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
};

const modal: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  padding: 24,
  width: 'min(720px, 90vw)',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontSize: 14,
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#4f46e5',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

const btnSecondary: React.CSSProperties = {
  padding: '10px 18px',
  background: '#fff',
  color: '#475569',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  fontWeight: 600,
  cursor: 'pointer',
  fontSize: 14,
};

export const CatalogAskModal: React.FC<Props> = ({ defaultCategoryId, onClose }) => {
  const [query, setQuery] = useState('');
  const [categoryId, setCategoryId] = useState(defaultCategoryId || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CatalogAskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAsk = async () => {
    if (!query.trim()) {
      setError('Digite uma pergunta');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await askCatalog(query.trim(), categoryId.trim() || undefined);
      setResult(res);
    } catch (err) {
      setError((err as Error).message || 'Erro ao consultar catálogo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔍 Testar catálogo</h3>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}
          >
            ×
          </button>
        </div>

        <p style={{ margin: '0 0 12px 0', fontSize: 13, color: '#64748b' }}>
          Faça uma pergunta como se fosse um lead. A IA responde apenas com base no catálogo cadastrado.
        </p>

        <div style={{ marginBottom: 10 }}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ex: liste cursos de tecnologia"
            style={inputStyle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleAsk();
              }
            }}
            autoFocus
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            placeholder="Filtrar por categoria (opcional, slug)"
            style={inputStyle}
          />
        </div>

        {error && (
          <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 20 }}>
          <button type="button" onClick={onClose} style={btnSecondary}>
            Fechar
          </button>
          <button type="button" onClick={handleAsk} style={{ ...btnPrimary, opacity: loading ? 0.6 : 1 }} disabled={loading}>
            {loading ? 'Consultando…' : 'Perguntar'}
          </button>
        </div>

        {result && (
          <>
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                Resposta da IA
              </h4>
              <div
                style={{
                  padding: 14,
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: 14,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {result.answer}
              </div>
            </div>

            <div>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, fontWeight: 700, color: '#374151' }}>
                Fontes citadas ({result.sources.length})
              </h4>
              {result.sources.length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8' }}>(nenhuma — verifique se há items indexados)</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                      <th style={{ padding: 8, fontWeight: 600 }}>Produto</th>
                      <th style={{ padding: 8, fontWeight: 600 }}>Seção</th>
                      <th style={{ padding: 8, fontWeight: 600, textAlign: 'right' }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sources.map((s, idx) => (
                      <tr key={idx} style={{ borderTop: '1px solid #e2e8f0' }}>
                        <td style={{ padding: 8 }}>{s.name}</td>
                        <td style={{ padding: 8, fontSize: 12, color: '#64748b' }}>{s.sectionType}</td>
                        <td style={{ padding: 8, textAlign: 'right', fontFamily: 'monospace' }}>
                          {s.score.toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CatalogAskModal;
